import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getAuth, getIdToken } from 'firebase/auth';
import firebaseApp from '../Firebase';
import DemoHeader from '../components/Demo/DemoHeader';
import DemoInput from '../components/Demo/DemoInput';
import EmailConsentForm from '../components/AIOnboarding/EmailConsentForm';
import AccountForm from '../components/AIOnboarding/AccountForm';
import ChatOnboardingSteps from '../components/AIOnboarding/ChatOnboardingSteps';
import TypingIndicator from '../components/Message/TypingIndicator';
import { getVisitorId } from '../utils/abTest';
import { sliderLabel, valueAtPosition, positionOfValue, rangeInputProps, sliderRange } from '../utils/sliderField';
import { getDevice } from '../hooks/useCommunityPageAnalytics';
import './Demo.css';
import './Messenger.css';
import '../components/AIOnboarding/AIOnboarding.css';
import { renderMessageText, ChunkReveal, typeDuration } from '../components/AIOnboarding/chunkText';
import { trackMeta, communityParams } from '../utils/metaPixel';

const API = `${process.env.REACT_APP_API_BASE_URL}/aiOnboarding`;

// A 1.5s "typing" pause before the first welcome message (as in the
// messenger), then only a brief flash of typing before each follow-on message.
const WELCOME_FIRST_DELAY_MS = 400;
const CARDS_DELAY_MS = 300;

// No pause between messages: each starts arriving as the last one finishes.
const TYPING_DOTS_MS = 0;

// The chat as a flat list of things to reveal one at a time: each message's
// text, and its suggestion cards as a separate step.
const toItems = (messages) => messages.flatMap((m, i) => [
  ...(m.content ? [{ key: `${i}-text`, msgIndex: i, kind: 'text', role: m.role }] : []),
  ...(m.suggestions?.length ? [{ key: `${i}-cards`, msgIndex: i, kind: 'cards', role: 'assistant' }] : [])
]);

// ?v=2 conversations are saved separately from v1 ones.
// …and demo chats (?mode=demo) separately from real ones.
const storageKey = (ref, variant) => `aiOnboardingSession:${String(ref).toLowerCase()}${variant === 2 ? ':v2' : variant === 'demo' ? ':demo' : ''}`;
const readStored = (ref, variant) => { try { return localStorage.getItem(storageKey(ref, variant)); } catch { return null; } };
const writeStored = (ref, variant, id) => { try { id ? localStorage.setItem(storageKey(ref, variant), id) : localStorage.removeItem(storageKey(ref, variant)); } catch { /* ignore */ } };

// On a local copy of the site (testing), reloading the page starts a fresh
// chat — on the live site a reload carries on where they were.
const reloadedLocally = () => {
  try {
    return /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)
      && performance.getEntriesByType('navigation')[0]?.type === 'reload';
  } catch { return false; }
};

const postJson = async (path, body, headers = {}) => {
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify(body)
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
};


// AI chat onboarding for ad traffic: /meet/:communityRef
// The slider for a number question asked in the chat (e.g. how far away
// people can be), starting at the community's default.
const ChatSlider = ({ config, onSend }) => {
  const start = Number.isFinite(Number(config.start)) ? Number(config.start) : sliderRange(config).min;
  const [value, setValue] = useState(start);
  return (
    <div className="aio-slider">
      <div className="aio-slider-value">{sliderLabel(value, config)}</div>
      <input
        type="range"
        {...rangeInputProps(config)}
        value={positionOfValue(value, config) ?? 0}
        onChange={(e) => setValue(valueAtPosition(e.target.value, config))}
        aria-valuetext={sliderLabel(value, config)}
      />
      <div className="aio-slider-scale" aria-hidden="true">
        <span>{sliderLabel(sliderRange(config).min, config)}</span>
        <span>{sliderLabel(sliderRange(config).max, config)}</span>
      </div>
      <button type="button" className="aio-choice aio-choice--done" onClick={() => onSend(value)}>Send</button>
    </div>
  );
};

// Date of birth in the chat: day / month / year, then Send.
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const ChatDate = ({ onSend }) => {
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: 93 }, (_, i) => thisYear - 18 - i);
  const ready = day && month && year;
  const send = () => {
    const iso = `${year}-${String(Number(month) + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    onSend(iso, `${Number(day)} ${MONTHS[Number(month)]} ${year}`);
  };
  return (
    <div className="aio-slider aio-date">
      <div className="aio-date-row">
        <select value={day} onChange={(e) => setDay(e.target.value)} aria-label="Day">
          <option value="">Day</option>
          {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Month">
          <option value="">Month</option>
          {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
        </select>
        <select value={year} onChange={(e) => setYear(e.target.value)} aria-label="Year">
          <option value="">Year</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <button type="button" className="aio-choice aio-choice--done" disabled={!ready} onClick={send}>Send</button>
    </div>
  );
};

const AIOnboarding = () => {
  const { communityRef } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // ?v=2: welcome with tappable "Find other … / Explain how it works" replies.
  const variant = new URLSearchParams(location.search).get('v') === '2' ? 2 : 1;
  // ?mode=demo: a community page's Demo button — the questions, then people
  // like them and an offer, then sign-up (backend offerFromDemo).
  const demoMode = new URLSearchParams(location.search).get('mode') === 'demo';
  const storeAs = demoMode ? 'demo' : variant;
  const [session, setSession] = useState(null); // public view from the API
  const [pending, setPending] = useState(null); // user message awaiting a reply
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [finishing, setFinishing] = useState(false);
  // New members finish their profile right here in the chat.
  const [onboardingInChat, setOnboardingInChat] = useState(false);
  // Options ticked so far on a profile question, plus their own "Other" answer
  // (typed into the Other option itself once tapped).
  const [selectedChoices, setSelectedChoices] = useState([]);
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherText, setOtherText] = useState('');
  // New bot messages (and suggestion cards) are revealed one at a time with
  // "… bot is typing" in between, like the messenger. `shown` = how many
  // items of toItems(messages) are visible.
  const [shown, setShownState] = useState(0);
  const shownRef = useRef(0);
  const setShown = (n) => { shownRef.current = n; setShownState(n); };
  const revealTimersRef = useRef([]);
  // Bot messages still arriving (or about to), by item key.
  const [typing, setTyping] = useState(() => new Set());
  const doneTyping = useCallback((key) => setTyping((prev) => { const next = new Set(prev); next.delete(key); return next; }), []);
  useEffect(() => () => revealTimersRef.current.forEach(clearTimeout), []);
  // First new bot item of the latest batch — scrolled to the top of the pane
  // so people start reading where the new messages begin.
  const anchorKeyRef = useRef(null);
  const messagesPaneRef = useRef(null);
  const hasScrolledRef = useRef(false);

  // The site-wide cookie banner is fixed to the bottom of the screen and would
  // cover the chat input — shrink the page by its height while it's showing.
  const [cookieBannerHeight, setCookieBannerHeight] = useState(0);
  useEffect(() => {
    let resizeObserver = null;
    const track = () => {
      const banner = document.querySelector('.cookie-banner');
      resizeObserver?.disconnect();
      resizeObserver = null;
      if (!banner) { setCookieBannerHeight(0); return; }
      resizeObserver = new ResizeObserver(() => setCookieBannerHeight(banner.offsetHeight));
      resizeObserver.observe(banner);
      setCookieBannerHeight(banner.offsetHeight);
    };
    const mutationObserver = new MutationObserver(track);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    track();
    return () => { mutationObserver.disconnect(); resizeObserver?.disconnect(); };
  }, []);
  const accountRef = useRef(null);
  const emailFormRef = useRef(null);

  // Show a new version of the conversation. The visitor's own messages appear
  // at once; new bot items follow one by one. `firstDelay` is the pause before
  // the first of them (0 when the typing indicator was already showing while
  // we waited for the server). `showAll` skips the animation (resuming).
  const applySession = useCallback((data, { firstDelay = 0, showAll = false } = {}) => {
    revealTimersRef.current.forEach(clearTimeout);
    revealTimersRef.current = [];
    setSession(data);
    const items = toItems(data.messages);
    if (showAll) {
      anchorKeyRef.current = null;
      setTyping(new Set());
      setShown(items.length);
      return;
    }
    let visibleNow = Math.min(shownRef.current, items.length);
    let at = 0;
    let firstBot = true;
    // How long the previous new bot message takes to arrive — the next one
    // waits for it.
    let prevTyping = 0;
    const toType = [];
    anchorKeyRef.current = null;
    for (let k = visibleNow; k < items.length; k++) {
      const item = items[k];
      if (item.role === 'assistant') {
        if (firstBot) {
          at += firstDelay;
          anchorKeyRef.current = item.key;
          firstBot = false;
        } else {
          at += prevTyping + (item.kind === 'cards' ? CARDS_DELAY_MS : TYPING_DOTS_MS);
        }
        const text = item.kind === 'text' ? data.messages?.[item.msgIndex]?.content : '';
        prevTyping = text ? typeDuration(text) : 0;
        if (text) toType.push(item.key);
      }
      const count = k + 1;
      if (at === 0) visibleNow = count;
      else revealTimersRef.current.push(setTimeout(() => setShown(count), at));
    }
    setTyping(new Set(toType));
    setShown(visibleNow);
  }, []);

  // Start (or resume) once per community — the ref also stops React's
  // development double-mount from creating two conversations.
  const startedForRef = useRef(null);
  useEffect(() => {
    if (startedForRef.current === communityRef) return;
    startedForRef.current = communityRef;
    const params = new URLSearchParams(location.search);
    const begin = async () => {
      setIsLoading(true);
      const { ok, status, data } = await postJson('/start', {
        communityRef,
        // ?new=1 always starts a fresh conversation (handy for testing);
        // ?session= carries a chat over when it's reopened in another browser.
        sessionId: params.get('new') || reloadedLocally() ? null : (params.get('session') || readStored(communityRef, storeAs)),
        demo: demoMode || undefined,
        // ?demo=<demo session>: carrying on from the demo (pages/Demo.js) —
        // its conversation comes across, then the questions.
        demoSessionId: params.get('demo') || undefined,
        // …and their answer there to the first question (pages/Demo.js).
        demoAnswer: params.get('demo') ? (() => { try { const a = JSON.parse(sessionStorage.getItem('demoFirstAnswer') || 'null'); sessionStorage.removeItem('demoFirstAnswer'); return a; } catch { return null; } })() : undefined,
        variant,
        visitorId: getVisitorId(),
        device: getDevice(),
        referrer: document.referrer || '',
        utm: {
          source: params.get('utm_source'),
          medium: params.get('utm_medium'),
          campaign: params.get('utm_campaign'),
          content: params.get('utm_content'),
          term: params.get('utm_term')
        }
      }).catch(() => ({ ok: false, status: 0, data: {} }));
      setIsLoading(false);
      if (status === 404) { setNotFound(true); return; }
      if (!ok) { setError(data.error || 'Could not start the chat — please refresh to try again.'); return; }
      writeStored(communityRef, storeAs, data.sessionId);
      if (data.revealFromMessage != null) {
        // From the demo: what was said there shows at once; the rest arrives
        // as usual. (?demo= comes off the address, so a reload carries on.)
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('demo');
          window.history.replaceState(null, '', url.pathname + url.search);
        } catch { /* ignore */ }
        const firstNew = toItems(data.messages).findIndex(i => i.msgIndex >= data.revealFromMessage);
        setShown(firstNew < 0 ? 0 : firstNew);
        applySession(data, { firstDelay: 250 });
        return;
      }
      const isFresh = !data.messages.some(m => m.role === 'user');
      applySession(data, isFresh ? { firstDelay: WELCOME_FIRST_DELAY_MS } : { showAll: true });
    };
    begin();
    // Only on first load for this community.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityRef]);

  // Scroll the chat pane itself, a frame later so new items are laid out:
  // - resuming: jump to the end;
  // - new bot messages: put the first of them at the top of the pane;
  // - otherwise (their own message, waiting for a reply): the end.
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const pane = messagesPaneRef.current;
      if (!pane || !session) return;
      if (!hasScrolledRef.current) {
        pane.scrollTo({ top: pane.scrollHeight, behavior: 'auto' });
        hasScrolledRef.current = true;
        return;
      }
      const anchor = !pending && anchorKeyRef.current && pane.querySelector(`[data-item="${anchorKeyRef.current}"]`);
      const top = anchor
        ? anchor.getBoundingClientRect().top - pane.getBoundingClientRect().top + pane.scrollTop - 12
        : pane.scrollHeight;
      // Instant: a smooth scroll gets cancelled when the input re-focuses.
      pane.scrollTo({ top, behavior: 'auto' });
    });
    return () => cancelAnimationFrame(id);
  }, [session, shown, pending, isLoading]);

  // After signing up, typed messages go to the post-sign-up chat
  // (ChatOnboardingSteps: "show me more" etc.), not the pre-sign-up one.
  const afterSignupRef = useRef(null);
  const handleSendMessage = useCallback(async (text, quickReply, choice) => {
    if (onboardingInChat && afterSignupRef.current) { afterSignupRef.current.say(text); return; }
    if (!session || isLoading) return;
    setError('');
    setPending(text);
    setIsLoading(true);
    setSelectedChoices([]);
    setOtherOpen(false);
    setOtherText('');
    const { ok, data } = await postJson('/message', { sessionId: session.sessionId, message: text, quickReply, choice })
      .catch(() => ({ ok: false, data: {} }));
    setIsLoading(false);
    setPending(null);
    if (!ok) { setError(data.error || 'The bot is having trouble right now — please try again in a moment.'); return; }
    applySession(data);
  }, [session, isLoading, applySession, onboardingInChat]);

  // Send the ticked options plus anything typed into "Other".
  const sendChoices = () => {
    const other = otherText.trim();
    const parts = [...selectedChoices, ...(other ? [other] : [])];
    if (!parts.length) return;
    handleSendMessage(parts.join(', '), undefined, selectedChoices.length ? { key: session.choices.key, values: selectedChoices } : undefined);
  };

  const handleEmailSubmit = useCallback(async (form) => {
    setError('');
    setIsLoading(true);
    const { ok, data } = await postJson('/email', { sessionId: session.sessionId, ...form })
      .catch(() => ({ ok: false, data: {} }));
    setIsLoading(false);
    if (!ok) return { error: data.error || 'Something went wrong — please try again.' };
    // A moment before the next message, even though saving is instant.
    applySession(data, { firstDelay: 250 });
    return {};
  }, [session, applySession]);

  // Account exists: save what the chat gathered, then continue to the
  // normal onboarding (new users) or the right place for existing users.
  const handleAuthenticated = useCallback(async ({ user, isNewUser, firstName, consents }) => {
    setFinishing(true);
    const token = await getIdToken(user || getAuth(firebaseApp).currentUser);
    const { ok, data } = await postJson('/complete', { sessionId: session.sessionId, consents }, { Authorization: `Bearer ${token}` })
      .catch(() => ({ ok: false, data: {} }));
    writeStored(communityRef, storeAs, null);
    if (ok && !data.member) {
      navigate(`/JoinAdditionalCommunity/${session.community.id}`);
    } else if (isNewUser) {
      // Meta Pixel: a sign-up, tagged with this community (utils/metaPixel.js).
      trackMeta('CompleteRegistration', { ...communityParams({ id: session.community?.id, title: session.community?.title, ref: session.community?.databaseRef }), signup_route: 'meet' });
      // The name they gave on the sign-up form, if the chat didn't have one.
      if (firstName) setSession((prev) => (prev && !prev.firstName ? { ...prev, firstName } : prev));
      setFinishing(false);
      setOnboardingInChat(true);
    } else {
      navigate('/messenger');
    }
  }, [session, communityRef, variant, navigate]);

  // "Interested in connecting": while they're looking at the people it's the
  // same as "Yes, connect me"; after that it takes them to the next form.
  const scrollToForm = () => {
    if (session?.stage === 'people') {
      handleSendMessage('Yes, connect me', 'connect');
      return;
    }
    (session?.stage === 'email' ? emailFormRef : accountRef)
      .current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // Back from an emailed log-in link (they already had an account): the
  // site signs them in (components/EmailLinkSignIn.js) and keeps them here —
  // finish on their behalf instead of showing the sign-up form again.
  // Only for a visit that arrived through the link — a sign-up in the chat
  // (Google, Facebook, email) finishes from the form itself, once their
  // account exists.
  const [cameFromEmailLink] = useState(() => {
    try { return new URL(window.location.href).searchParams.get('mode') === 'signIn' && window.location.href.includes('oobCode='); } catch { return false; }
  });
  const autoFinishedRef = useRef(false);
  useEffect(() => {
    if (!cameFromEmailLink || autoFinishedRef.current || session?.stage !== 'account' || !session?.accountConfirmed) return undefined;
    const auth = getAuth(firebaseApp);
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user || autoFinishedRef.current) return;
      autoFinishedRef.current = true;
      handleAuthenticated({ user, isNewUser: false });
    });
    return unsubscribe;
  }, [session?.stage, session?.accountConfirmed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Whatever comes under the messages to tap or fill in (answer buttons, the
  // slider, the date, quick replies, the sign-up form) appears after a
  // moment of the bot "typing", once the last message has landed.
  const itemCount = toItems(session?.messages || []).length;
  const stillRevealing = shown < itemCount || typing.size > 0;
  const [widgetsReady, setWidgetsReady] = useState(false);
  useEffect(() => {
    if (stillRevealing || isLoading) { setWidgetsReady(false); return undefined; }
    const id = setTimeout(() => setWidgetsReady(true), 250);
    return () => clearTimeout(id);
  }, [stillRevealing, isLoading, itemCount]);

  if (notFound) {
    return (
      <div className="demo-container aio-centered">
        <p>Sorry, we couldn't find that community.</p>
        <button className="demo-signup-button aio-narrow" onClick={() => navigate('/')}>Go to The Interconnector</button>
      </div>
    );
  }

  const community = session?.community;
  const messages = session?.messages || [];
  const items = toItems(messages);
  const visibleItems = items.slice(0, shown);
  // A message still arriving counts as revealing (no "typing…" dots under
  // it, nothing to tap until it's all there).
  const typingNow = visibleItems.some(i => typing.has(i.key));
  const revealing = shown < items.length || typingNow;
  const stage = session?.stage;
  const emailFormIndex = session?.emailPromptIndex;
  // Older conversations didn't record where the email form went: show it last.
  const emailFormAtEnd = stage === 'email' && (emailFormIndex === null || emailFormIndex === undefined);

  const emailForm = (
    <div className="demo-message bot" ref={emailFormRef}>
      <div className="demo-message-content aio-widget">
        <EmailConsentForm
          key={stage === 'email' ? 'open' : 'done'}
          onSubmit={handleEmailSubmit}
          submitted={stage === 'email' ? null : {
            email: session?.email,
            notifyConsent: session?.notifyConsent,
            deleteIfInactive: session?.deleteIfInactive
          }}
        />
      </div>
    </div>
  );

  return (
    <div
      className="demo-container aio-page"
      style={cookieBannerHeight ? { height: `calc(100vh - ${cookieBannerHeight}px)` } : undefined}
    >
      <div className="demo-chatbox-panel demo-panel-active">
        <div className="demo-chatbox-wrapper">
          <div className="demo-chatbox-component">
            <div className="demo-chatbox-header">
              {/* Empty until the community has loaded — no generic placeholder title. */}
              {community ? (
                <DemoHeader
                  title={`${community.title} Bot`}
                  image={community.image}
                  backTo={`/${community.databaseRef}`}
                />
              ) : (
                <div className="demo-header-container" />
              )}
            </div>

            <div
              className={`demo-chatbox-messages${community?.backgroundImage ? ' aio-has-bg' : ''}`}
              ref={messagesPaneRef}
              style={community?.backgroundImage ? { '--aio-bg': `url("${community.backgroundImage}")` } : undefined}
            >
              <div className="demo-messages">
                {visibleItems.map((item) => {
                  const message = messages[item.msgIndex];
                  return (
                    <div key={item.key} data-item={item.key}>
                      {item.kind === 'text' && (
                        <div className={`demo-message ${message.role === 'user' ? 'user' : 'bot'}`}>
                          <div className="demo-message-content">
                            <div className="demo-message-text">
                              {message.role !== 'user' && typing.has(item.key)
                                ? <ChunkReveal text={message.content} onDone={() => doneTyping(item.key)} />
                                : renderMessageText(message.content)}
                            </div>
                            {message.timestamp && (
                              <span className="demo-timestamp">
                                {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {item.kind === 'cards' && (
                        <div className="demo-cards-container">
                          <div className="demo-people-cards">
                            {message.suggestions.map((person, si) => (
                              <div key={si} className="demo-person-card">
                                <div className="demo-person-avatar">
                                  <div className={`demo-avatar-placeholder demo-avatar-${(person.name.charCodeAt(0) % 5) + 1}`}>
                                    <span className="demo-avatar-initials">{person.name.charAt(0)}</span>
                                  </div>
                                </div>
                                <div className="demo-person-content">
                                  <h4>{person.name}</h4>
                                  {(person.ageRange || person.location) && (
                                    <div className="demo-person-details">
                                      {person.ageRange && <span className="demo-person-age">{person.ageRange}</span>}
                                      {person.location && <span className="demo-person-gender">{person.location}</span>}
                                    </div>
                                  )}
                                  <p>{person.bio}</p>
                                  {!message.examples && ['people', 'email', 'account'].includes(stage) && (
                                    <button className="demo-connect-btn" onClick={scrollToForm}>
                                      Interested in connecting
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          <p className="aio-privacy-note">
                            {message.examples ? 'Example profiles, not real members.' : 'Names and some details changed for privacy.'}
                          </p>
                        </div>
                      )}

                      {/* Demo: Start Demo / Sign Up Now under the welcome, as in the old
                          demo — once started, "Demo Active" stays and Sign Up Now goes. */}
                      {session?.demoMode && item.kind === 'text' && item.msgIndex === 0 && (session.demoStarted || (widgetsReady && !revealing)) && (
                        <div className="demo-signup-button-container demo-gate-buttons">
                          <button
                            type="button"
                            className={`demo-signup-button demo-gate-demo-button ${session.demoStarted || pending ? 'demo-gate-demo-button--active' : ''}`}
                            disabled={session.demoStarted || !!pending || isLoading}
                            onClick={() => handleSendMessage('Start Demo', 'demo_start')}
                          >
                            <span className="demo-gate-label">{session.demoStarted || pending ? 'Demo Active' : 'Start Demo'}</span>
                          </button>
                          {!session.demoStarted && !pending && (
                            <button type="button" className="demo-signup-button" onClick={() => navigate(`/groupsearch/join/${session?.community?.id}`)}>
                              Sign Up Now
                            </button>
                          )}
                        </div>
                      )}

                      {/* The email form stays where it first appeared — greyed out once sent. */}
                      {item.kind === 'text' && item.msgIndex === emailFormIndex && emailForm}
                    </div>
                  );
                })}

                {/* The current profile question's options, tappable. One-answer
                    questions send on tap; several-answer ones tick, then Done. */}
                {/* A slider question (e.g. "Suggest people within"): drag, then Send. */}
                {session?.choices?.slider && widgetsReady && !revealing && !isLoading && !pending && (
                  <ChatSlider
                    key={session.choices.key}
                    config={session.choices.slider}
                    onSend={(value) => handleSendMessage(sliderLabel(value, session.choices.slider), undefined, { key: session.choices.key, values: [value] })}
                  />
                )}

                {/* Date of birth: a date picker, then Send. */}
                {session?.choices?.date && widgetsReady && !revealing && !isLoading && !pending && (
                  <ChatDate onSend={(iso, label) => handleSendMessage(label, undefined, { key: session.choices.key, values: [iso] })} />
                )}

                {session?.choices && !session.choices.slider && !session.choices.date && widgetsReady && !revealing && !isLoading && !pending && (
                  <div className="aio-choices">
                    {/* A community's own "Other" option is covered by "Other…" (type your own). */}
                    {session.choices.options.filter(option => !/^other$/i.test(String(option).trim())).map(option => {
                      const on = selectedChoices.includes(option);
                      return (
                        <button
                          key={option}
                          type="button"
                          className={`aio-choice${on ? ' aio-choice--on' : ''}`}
                          onClick={() => {
                            if (!session.choices.multi) {
                              handleSendMessage(option, undefined, { key: session.choices.key, values: [option] });
                            } else {
                              setSelectedChoices(prev => on ? prev.filter(o => o !== option) : [...prev, option]);
                            }
                          }}
                        >
                          {session.choices.multi && on ? '✓ ' : ''}{option}
                        </button>
                      );
                    })}
                    {otherOpen ? (
                      <input
                        className="aio-choice aio-choice-other"
                        autoFocus
                        placeholder="Other…"
                        value={otherText}
                        maxLength={200}
                        onChange={(e) => setOtherText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendChoices(); } }}
                      />
                    ) : (
                      <button type="button" className="aio-choice" onClick={() => setOtherOpen(true)}>
                        Other…
                      </button>
                    )}
                    {(session.choices.multi || otherOpen) && (
                      <button
                        type="button"
                        className="aio-choice aio-choice--done"
                        disabled={!selectedChoices.length && !otherText.trim()}
                        onClick={sendChoices}
                      >
                        {session.choices.multi ? 'Done' : 'Send'}
                      </button>
                    )}
                  </div>
                )}

                {session?.quickReplies?.length > 0 && !session.quickReplies.some(r => r.id === 'demo_start') && widgetsReady && !revealing && !isLoading && !pending && (
                  <div className="aio-quick-replies">
                    {session.quickReplies.map(reply => (
                      <button
                        key={reply.id}
                        type="button"
                        className="aio-quick-reply"
                        onClick={() => (reply.id === 'demo_signup'
                          // The demo's "Sign Up Now": the community's sign-up page.
                          ? navigate(`/groupsearch/join/${session?.community?.id}`)
                          : handleSendMessage(reply.label, reply.id))}
                      >
                        {reply.label}
                      </button>
                    ))}
                  </div>
                )}

                {pending && (
                  <div className="demo-message user">
                    <div className="demo-message-content">
                      <div className="demo-message-text"><p className="demo-paragraph">{pending}</p></div>
                    </div>
                  </div>
                )}

                {emailFormAtEnd && !revealing && emailForm}

                {onboardingInChat && (
                  <ChatOnboardingSteps
                    ref={afterSignupRef}
                    communityId={community?.id}
                    communityTitle={community?.title}
                    firstName={session?.firstName}
                    sessionId={session?.sessionId}
                    communityPic={community?.image}
                    onFinished={() => navigate(`/messenger?from=onboarding&communityId=${community?.id}#messenger-chatbox`)}
                  />
                )}

                {stage === 'account' && session.accountConfirmed && !onboardingInChat && widgetsReady && !isLoading && !revealing && (
                  <div className="demo-message bot" ref={accountRef}>
                    <div className="demo-message-content aio-widget">
                      {finishing ? (
                        <p className="demo-paragraph">Setting up your profile…</p>
                      ) : (
                        <AccountForm
                          showNoCommitment={!!community?.showNoCommitmentSignup}
                          email={session.email}
                          firstName={session.firstName}
                          communityId={community?.id}
                          sessionId={session.sessionId}
                          onAuthenticated={handleAuthenticated}
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* "… bot is typing" only while really waiting on the server —
                    never as a made-up pause between messages. */}
                {isLoading && session && (
                  <TypingIndicator isBot communityName={community?.title || 'Community'} />
                )}

                {error && <div className="aio-error aio-error-banner">{error}</div>}

              </div>
            </div>

            <div className="demo-chatbox-input">
              <DemoInput onSendMessage={handleSendMessage} isLoading={isLoading || !session || finishing || revealing} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIOnboarding;
